import { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { Search } from '@shared/components/ui/Search';
import { ColumnSelector } from '@shared/components/ui/ColumnSelector';
import { Button } from '@shared/components/ui/button';
import { ModalCenter, type ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Popover, PopoverContent, PopoverTrigger } from '@shared/components/ui/popover';
import { useIsMobile } from '@shared/hooks/useMobile';
import { faSliders } from '@fortawesome/pro-light-svg-icons';
import { cn } from '@shared/utils/utils';
import { ChangeEvent, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IncidentsFiltersPanel } from './IncidentsFiltersPanel';

interface FiltersSectionProps {
  handleSearch: (value: string) => void;
  onFilterChange?: (conditions: FilterConditionOutput[]) => void;
  allColumns: any[];
  selectedColumns: string[];
  onColumnsChange: (columns: string[]) => void;
  fixedColumns?: string[];
  searchTerm: string;
  filterConditions: FilterConditionOutput[];
  shopIds?: string[];
  teamIds?: string[];
  courierIds?: string[];
  onShopChange?: (ids: string[]) => void;
  onTeamChange?: (ids: string[]) => void;
  onCourierChange?: (ids: string[]) => void;
  onClearAll?: () => void;
}

/**
 * Filtros de incidencias: Popover en desktop (evita conflicto Radix DropdownMenu + MultiSelect anidados)
 * y Modal en mobile, mismo patrón visual que FiltersDropdown.
 */
export const FiltersSection = ({
  handleSearch,
  onFilterChange,
  allColumns,
  selectedColumns,
  onColumnsChange,
  fixedColumns = [],
  searchTerm,
  filterConditions,
  shopIds = [],
  teamIds = [],
  courierIds = [],
  onShopChange,
  onTeamChange,
  onCourierChange,
  onClearAll,
}: FiltersSectionProps) => {
  const { t } = useTranslation('incidentsModule');
  const [inputValue, setInputValue] = useState(searchTerm);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const isMobile = useIsMobile();
  const modalRef = useRef<ModalCenterRef>(null);

  const filterTitle = t('filters.title', 'Filtros');

  const activeFiltersCount = useMemo(() => {
    let count = filterConditions.length;
    const hasShopInConditions = filterConditions.some((c) => c.column === 'shop_id');
    const hasCourierInConditions = filterConditions.some((c) => c.column === 'courier_id');
    const hasTeamInConditions = filterConditions.some((c) => c.column === 'team_id');
    if (shopIds.length > 0 && !hasShopInConditions) count += 1;
    if (courierIds.length > 0 && !hasCourierInConditions) count += 1;
    if (teamIds.length > 0 && !hasTeamInConditions) count += 1;
    return count;
  }, [filterConditions, shopIds, teamIds, courierIds]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleSearchSubmit = () => {
    handleSearch(inputValue.trim());
  };

  const handleFiltersOpenChange = (open: boolean) => {
    setFiltersOpen(open);
  };

  const filtersPanel = (
    <IncidentsFiltersPanel
      initialConditions={filterConditions}
      onChange={onFilterChange}
      shopIds={shopIds}
      teamIds={teamIds}
      courierIds={courierIds}
      onShopChange={onShopChange}
      onTeamChange={onTeamChange}
      onCourierChange={onCourierChange}
      onClearAll={onClearAll}
    />
  );

  /* Trigger - Botón único enfocable (sin div wrapper) */
  const triggerButton = (
    <Button
      variant="outline"
      type="button"
      size="sm"
      icon={faSliders}
      iconPosition="start"
      className={cn(
        'relative shadow-none transition-colors border border-input bg-background text-foreground/70 hover:bg-accent hover:text-accent-foreground hover:border-input',
      )}
      data-testid="filters-toggle-button"
      onClick={
        isMobile
          ? () => {
              modalRef.current?.openModal();
            }
          : undefined
      }
    >
      <span className="hidden sm:block text-sm">{filterTitle}</span>
      {activeFiltersCount > 0 && (
        <span
          className="absolute -top-2 -right-2 bg-primary text-btn-text text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium shadow-sm z-10"
          aria-label={`${activeFiltersCount} filtros activos`}
        >
          {activeFiltersCount}
        </span>
      )}
    </Button>
  );

  return (
    <>
      <Search
        placeholder={t('filters.search_placeholder')}
        value={inputValue}
        onChange={handleInputChange}
        onEnter={handleSearchSubmit}
        dataTestId="incidents-search"
        inputDataTestId="incidents-search-input"
      />

      {isMobile ? (
        <>
          {triggerButton}
          <ModalCenter
            ref={modalRef}
            title={filterTitle}
            type="default"
            width="xl"
            maxHeight="min(90vh, 720px)"
            className="w-full max-w-none"
            classNameBody="min-h-0 overflow-y-auto overscroll-contain p-3 pt-0"
          >
            {filtersPanel}
          </ModalCenter>
        </>
      ) : (
        <Popover open={filtersOpen} onOpenChange={handleFiltersOpenChange} modal={false}>
          <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
          <PopoverContent
            align="start"
            side="bottom"
            className="sm:min-w-[700px] sm:max-w-[700px] p-4 bg-body-bg text-foreground border border-card-border flex flex-col overflow-hidden"
          >
            <div className="flex shrink-0 items-center justify-between mb-2 pt-1">
              <div className="text-sm font-semibold text-foreground">{filterTitle}</div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain">{filtersPanel}</div>
          </PopoverContent>
        </Popover>
      )}

      <ColumnSelector
        columns={allColumns}
        selected={selectedColumns}
        onColumnsChange={onColumnsChange}
        fixedColumns={fixedColumns}
        storageKey="incidents-list"
        dataTestId="incidents-column-selector"
      />
    </>
  );
};
